import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import character from '../../assets/images/intake/step5/char.svg';
import { useIntakeFormContext } from '../../hooks/use-form-context.hooks';
import {
  IntakeFormWorkHistoryDTO,
  WorkHistory as WorkHistoryValue,
} from '../../redux/slice/form-intake';
import { dispatchUpdateIntakeFormWorkHistory } from '../../redux/slice/form-intake/form-intake.operations';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface WorkHistoryProps {
  step: number;
  onBack: () => void;
}

const history_options = [
  {
    value: WorkHistoryValue.LOOKING_FIRST_JOB,
    label: 'I am looking for my first job',
  },
  {
    value: WorkHistoryValue.NEVER_WORKED,
    label: 'I have never worked',
  },
  {
    value: WorkHistoryValue.RECENT_VOLUNTEER,
    label: 'I have recently worked as a volunteer',
  },
  {
    value: WorkHistoryValue.WORK_FULL_TIME,
    label: 'I have worked full time (30 or more hours a week)',
  },
  {
    value: WorkHistoryValue.WORK_PART_TIME,
    label: 'I have worked part time/casual time (less than 30 hours a week)',
  },
  {
    value: WorkHistoryValue.SELF_EMPLOYED,
    label: 'I have worked in my own business (self-employed)',
    is_editable: true,
  },
  {
    value: WorkHistoryValue.OTHER,
    label: 'Other',
    is_editable: true,
  },
];
export const WorkHistory: React.FC<WorkHistoryProps> = ({ step, onBack }) => {
  const navigate = useNavigate();
  const { workHistory, workHistorySelfEmployedComments, workHistoryOtherComments } =
    useIntakeFormContext();
  const [selectedWorkHistory, setSelectedWorkHistory] = useState(workHistory || '');
  const [selfRemarks, setSelfRemarks] = useState(workHistorySelfEmployedComments);
  const [otherRemarks, setOtherRemarks] = useState(workHistoryOtherComments);
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();

  const textAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>, isOther: boolean) => {
    if (!isOther) setSelfRemarks(event.target.value);
    else setOtherRemarks(event.target.value);
    setError('');
  };

  const handleWorkHistoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedWorkHistory(event.target.value);
    setError('');
  };

  useEffect(() => {
    window.scrollTo(0, 0); // Reset scroll to the top
  }, []);
  useEffect(() => {
    setSelectedWorkHistory(workHistory ?? '');
    setSelfRemarks(workHistorySelfEmployedComments ?? '');
    setOtherRemarks(workHistoryOtherComments ?? '');
  }, [workHistory, workHistorySelfEmployedComments, workHistoryOtherComments]);

  const { handleSubmit } = useForm<FormData>();
  const submitWorkHistory: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted work history form.', {
      selectedWorkHistory,
      selfRemarks,
      otherRemarks,
    });
    const isSelfEmployedSelected: boolean = selectedWorkHistory.includes(
      WorkHistoryValue.SELF_EMPLOYED,
    );
    const isOtherSelected: boolean = selectedWorkHistory.includes(WorkHistoryValue.OTHER);
    if (!selectedWorkHistory) {
      setError('Please select an option');
    } else if (isSelfEmployedSelected && !selfRemarks) {
      setError('Please explain the self-employed category');
    } else if (isOtherSelected && !otherRemarks) {
      setError('Please explain the other category');
    } else {
      const dto: IntakeFormWorkHistoryDTO = {
        workHistory: selectedWorkHistory as WorkHistoryValue,
        workHistorySelfEmployedComments: isSelfEmployedSelected ? selfRemarks : undefined,
        workHistoryOtherComments: isOtherSelected ? otherRemarks : undefined,
      };
      await dispatch(dispatchUpdateIntakeFormWorkHistory(dto));
      navigate(`/pre-employment/intake/step${step + 1}`);
    }
  };

  const backButtonClick = () => {
    onBack();
  };

  return (
    <div>
      <div className="flex mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">06</div>
        <div>
          <h4 className="text-2xl">
            Which of the following best describes your
            <span className="font-semibold"> previous work history</span>
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 left-[-130px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className="md:px-10"
        action=""
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitWorkHistory)}
      >
        <div className="grid gap-y-3">
          {history_options.map((option) => (
            <div
              key={option.value}
              className={` ${option.is_editable ? 'h-[170px]' : 'h-[60px]'} `}
            >
              <label className="radio-container h-full p-4 block relative">
                <input
                  checked={selectedWorkHistory === option.value}
                  onChange={handleWorkHistoryChange}
                  value={option.value}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  name="work history"
                />
                <span className="checkmark font-semibold !block h-auto pt-4">
                  {option.label}
                  {option.is_editable && (
                    <textarea
                      onChange={(event) =>
                        textAreaChange(event, option.value.includes(WorkHistoryValue.OTHER))
                      }
                      placeholder="Explain"
                      value={
                        option.value.includes(WorkHistoryValue.OTHER) ? otherRemarks : selfRemarks
                      }
                      rows={2}
                      className="bg-white border rounded-lg w-full resize-none mt-4 p-3 font-light"
                      name=""
                    ></textarea>
                  )}
                </span>
              </label>
            </div>
          ))}
          {error && <p className="text-red-500">{error}</p>}
        </div>
        <div className="flex justify-between mt-8">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
